<template>
    <canvas ref="canvas" class="christmas-snow" aria-hidden="true"></canvas>
</template>

<script>
// Neve leve num canvas que preenche o elemento pai (cabeçalho, menu lateral, cartões). O pai precisa de
// position e overflow: hidden. Pausa com a aba em segundo plano e fora da tela; com "reduzir movimento" não anima.
import {prefersReducedMotion} from '@/Helpers/christmas.js';

const rand = (min, max) => min + Math.random() * (max - min);

function buildFlake(shaded) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 32;
    const ctx = canvas.getContext('2d');
    const paint = (stops, offsetY = 0) => {
        const gradient = ctx.createRadialGradient(16, 16 + offsetY, 0, 16, 16 + offsetY, 16);
        stops.forEach(([at, color]) => gradient.addColorStop(at, color));
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 32, 32);
    };
    // Sobre fundo claro o floco leva uma sombra verde suave para aparecer
    if (shaded) paint([[0, 'rgba(14, 94, 78, 0.45)'], [0.45, 'rgba(14, 94, 78, 0.18)'], [1, 'rgba(14, 94, 78, 0)']], 2);
    paint([[0, 'rgba(255, 255, 255, 1)'], [0.42, 'rgba(255, 255, 255, 0.95)'], [0.65, 'rgba(255, 255, 255, 0.35)'], [1, 'rgba(255, 255, 255, 0)']]);
    return canvas;
}

export default {
    name: 'ChristmasSnow',
    props: {
        // Flocos a cada 10.000 px² do elemento
        density: {type: Number, default: 1.2},
        // Fundo claro: sombra verde no floco
        shaded: {type: Boolean, default: false},
        maxSize: {type: Number, default: 7},
        speed: {type: Number, default: 1},
    },
    watch: {
        shaded(value) {
            this.sprite = buildFlake(value);
        },
    },
    mounted() {
        if (prefersReducedMotion()) return;
        this.ctx = this.$refs.canvas.getContext('2d');
        this.sprite = buildFlake(this.shaded);
        this.flakes = [];
        this.visible = true;
        this.last = performance.now();
        this.clock = 0;
        this.tick = this.tick.bind(this);
        this.onVisibility = this.onVisibility.bind(this);

        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.$el.parentElement);
        // Fora da tela (cartão rolado para longe, menu fechado) não desenha
        this.intersection = new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            this.restart();
        });
        this.intersection.observe(this.$el);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.resize();
        this.restart();
    },
    beforeUnmount() {
        cancelAnimationFrame(this.frame);
        this.resizeObserver?.disconnect();
        this.intersection?.disconnect();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    },
    methods: {
        makeFlake(anywhere) {
            const depth = Math.pow(Math.random(), 1.3);
            return {
                x: Math.random() * this.width,
                y: anywhere ? Math.random() * this.height : rand(-12, -4),
                depth,
                size: 1.6 + depth * (this.maxSize - 1.6),
                fall: (8 + depth * 26) * this.speed,
                sway: rand(4, 12) * (0.4 + depth),
                swaySpeed: rand(0.3, 0.8),
                phase: Math.random() * Math.PI * 2,
                alpha: 0.55 + depth * 0.45,
            };
        },
        resize() {
            const parent = this.$el.parentElement;
            const canvas = this.$refs.canvas;
            if (!parent || !canvas) return;
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            this.width = parent.clientWidth;
            this.height = parent.clientHeight;
            canvas.width = Math.round(this.width * ratio);
            canvas.height = Math.round(this.height * ratio);
            this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            const target = Math.round(Math.min(220, (this.width * this.height) / 10000 * this.density));
            while (this.flakes.length < target) this.flakes.push(this.makeFlake(true));
            this.flakes.length = target;
        },
        restart() {
            cancelAnimationFrame(this.frame);
            if (this.visible && !document.hidden) {
                this.last = performance.now();
                this.frame = requestAnimationFrame(this.tick);
            }
        },
        onVisibility() {
            this.restart();
        },
        tick(now) {
            const dt = Math.min(0.05, (now - this.last) / 1000);
            this.last = now;
            this.clock += dt;
            const {ctx, width, height} = this;
            ctx.clearRect(0, 0, width, height);
            const wind = Math.sin(this.clock * 0.23) * 6 + Math.sin(this.clock * 0.09) * 4;
            for (const flake of this.flakes) {
                flake.y += flake.fall * dt;
                flake.x += wind * (0.4 + flake.depth) * dt;
                if (flake.y - flake.size > height) Object.assign(flake, this.makeFlake(false));
                if (flake.x < -10) flake.x += width + 20;
                if (flake.x > width + 10) flake.x -= width + 20;
                const x = flake.x + Math.sin(this.clock * flake.swaySpeed + flake.phase) * flake.sway;
                ctx.globalAlpha = flake.alpha;
                ctx.drawImage(this.sprite, x - flake.size / 2, flake.y - flake.size / 2, flake.size, flake.size);
            }
            ctx.globalAlpha = 1;
            this.frame = requestAnimationFrame(this.tick);
        },
    },
};
</script>

<style scoped>
/* z-index -1: fica sobre o fundo do pai, mas atrás do conteúdo dele (o pai precisa formar um contexto de empilhamento) */
.christmas-snow {
    position: absolute;
    inset: 0;
    z-index: -1;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
    .christmas-snow {
        display: none;
    }
}
</style>
